import type { ChartSeries, Granularity } from './series';

export const formatNumber = new Intl.NumberFormat().format;

const weekdayDay = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric' });
const dayMonth = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
const fullDay = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const hourMinute = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function seriesColor(series: Pick<ChartSeries, 'slot'>): string {
  return series.slot === null ? 'var(--series-other)' : `var(--series-${series.slot + 1})`;
}

/** What one bar covers, e.g. "Fri 25 Sep, 09:00–10:00". */
export function columnLabel(start: Date, end: Date, granularity: Granularity): string {
  if (granularity === 'day') {
    return fullDay.format(start);
  }
  return `${fullDay.format(start)}, ${hourMinute.format(start)}–${hourMinute.format(end)}`;
}

export interface AxisTick {
  index: number;
  label: string;
}

/** X-axis labels: hours for a single day, midnights for a week, spaced days beyond. */
export function axisTicks(columns: Date[], granularity: Granularity): AxisTick[] {
  if (granularity === 'day') {
    const step = Math.ceil(columns.length / 8);
    return columns
      .map((date, index) => ({ index, label: dayMonth.format(date) }))
      .filter((tick) => tick.index % step === 0);
  }
  if (columns.length <= 25) {
    return columns
      .map((date, index) => ({ index, label: hourMinute.format(date) }))
      .filter((tick) => columns[tick.index].getHours() % 3 === 0);
  }
  return columns
    .map((date, index) => ({ index, label: weekdayDay.format(date) }))
    .filter((tick) => columns[tick.index].getHours() === 0);
}

/** A rounded axis maximum and up to four gridlines beneath it. */
export function niceScale(max: number): { max: number; ticks: number[] } {
  if (max <= 0) {
    return { max: 4, ticks: [0, 1, 2, 3, 4] };
  }
  const raw = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((factor) => factor * magnitude).find((value) => value >= raw)!;
  const stepped = Math.max(1, step);
  const top = Math.ceil(max / stepped) * stepped;
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += stepped) {
    ticks.push(value);
  }
  return { max: top, ticks };
}
